'use client';

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  LogOut,
  PanelLeft,
  PanelLeftClose,
  MousePointerClick,
  Menu,
  X,
  Sidebar as SidebarIcon,
} from 'lucide-react';
import { cn } from '../../utils';
import { SELECTION_BAR } from './selectionBar';
import {
  DEFAULT_SIDEBAR_MODE_KEY,
  readSidebarMode,
  persistSidebarState,
  type SidebarMode,
} from '../../hooks/useSidebarMode';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './collapsible';
import { SimpleTooltip } from './simple-tooltip';

/**
 * AppSidebar — THE app-shell rail, for every product-register app.
 *
 * This is the console sidebar's chrome, lifted out of the console's data
 * coupling so the internal staff tool renders the SAME navigation instead of a
 * second one. Before this existed, `apps/internal` hand-rolled a fixed 224px
 * panel with its own NavLink styling and a hardcoded `--app-content-inset`
 * literal — no collapse, no hover mode, no ⌘B, no collapsed-rail tooltips, no
 * mobile drawer, no focus ring on the primary navigation, and a width that
 * contradicted the design tokens. One concept, two implementations, and the
 * staff one was missing every affordance the customer one had.
 *
 * What stays HERE: geometry, modes, keyboard, active-item treatment, tooltips,
 * the mobile drawer. What stays at the CALL SITE: which entries exist, which is
 * active, prefetching, and the identity/footer content — i.e. everything that
 * needs an app's own router table or auth. `console-sidebar.tsx` and
 * `apps/internal`'s `InternalSidebar` are both thin adapters over this.
 *
 * Widths come from tokens.css (`--app-sidebar-*-w`) — THE single source of
 * truth. Never reintroduce a px literal or a TS constant for these.
 */

const EXPANDED_W = 'var(--app-sidebar-expanded-w)';
const COLLAPSED_W = 'var(--app-sidebar-collapsed-w)';
const HOVER_COLLAPSE_DELAY = 100;

const DEFAULT_LOGO_MARK = (
  <img
    src="/knitknot-logo-icon.svg"
    alt="KnitKnot"
    className="h-6 w-6 flex-shrink-0"
    style={{ imageRendering: 'crisp-edges' }}
    onError={(event) => {
      event.currentTarget.style.display = 'none';
      const parent = event.currentTarget.parentElement;
      if (parent && !parent.querySelector('.icon-fallback')) {
        const fallback = document.createElement('div');
        fallback.className =
          'icon-fallback flex h-6 w-6 flex-shrink-0 items-center justify-center rounded bg-primary font-semibold text-primary-foreground';
        fallback.textContent = 'K';
        parent.appendChild(fallback);
      }
    }}
  />
);

const DEFAULT_WORDMARK = (
  <span className="inline-flex h-7 flex-shrink-0 items-center">
    <img src="/knitknot-logo.svg" alt="KnitKnot" className="h-7 w-auto dark:hidden" />
    <img
      src="/knitknot-logo-dark.svg"
      alt="KnitKnot"
      className="hidden h-7 w-auto dark:block"
    />
  </span>
);

// ─── Shared link classes ────────────────────────────────────────
// Single source of truth for nav item styling — prevents expanded/collapsed drift.
// `focus-ring` is the canonical keyboard focus treatment (base.css). Without it
// the PRIMARY NAVIGATION is invisible to keyboard focus. Every consumer of
// NAV_LINK_BASE inherits it — do not drop it.
const NAV_LINK_LOCKED = 'cursor-not-allowed text-sidebar-foreground/35';

const NAV_LINK_BASE =
  'focus-ring group relative flex h-10 items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-150';
const NAV_LINK_ACTIVE = 'text-sidebar-accent-foreground';
const NAV_LINK_INACTIVE = 'text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground';
const NAV_ICON_CONTAINER = 'flex items-center justify-center h-5 w-5 flex-shrink-0';
const NAV_ICON_ACTIVE = 'h-[18px] w-[18px] text-primary';
const NAV_ICON_INACTIVE =
  'h-[18px] w-[18px] text-sidebar-foreground/55 group-hover:text-sidebar-accent-foreground transition-colors';
// Canonical selected-item accent bar — shared with StatCell + any selectable
// surface. The shared core is sizeless; this small centered h-3 fits a nav icon.
const ACTIVE_BAR = cn(SELECTION_BAR, 'top-1/2 h-5 -translate-y-1/2 -left-[9.5px]');

export interface AppNavItem {
  key: string;
  title: string;
  /** For an external item this is the FINAL href — the adapter resolves it. */
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
  isExternal?: boolean;
  /** External links open a new tab by default. Set false for app-switch links
   * that are part of one back-and-forth workflow. */
  newTab?: boolean;
  /** Rendered greyed and inert, with `lockedHint` on hover. */
  locked?: boolean;
  /** Pinned to the bottom of the rail, outside the scrollable area. */
  bottom?: boolean;
  /** Optional count badge, shown only when expanded and > 0. */
  badge?: number;
  /** Optional keyboard chord, e.g. `G P` — consumed by an app's hotkey layer
   *  (useNavHotkeys + its menu). Deliberately NOT rendered in the rail: the
   *  hover chips read as noise on every item (Max, 2026-08-22). */
  shortcut?: string;
}

interface AppNavSection {
  type: 'section';
  /** An empty title renders a divider rather than a label. */
  title: string;
}

export interface AppNavGroup {
  type: 'group';
  key: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: AppNavItem[];
}

export type AppNavEntry = AppNavSection | AppNavGroup | AppNavItem;

function isAppNavItem(entry: AppNavEntry): entry is AppNavItem {
  return !('type' in entry);
}

function isAppNavGroup(entry: AppNavEntry): entry is AppNavGroup {
  return 'type' in entry && entry.type === 'group';
}

interface AppSidebarProps {
  entries: AppNavEntry[];
  /** Longest-prefix (or app-specific) active test. External items never ask. */
  isActive: (href: string) => boolean;
  /** Warm a route on hover. Omit where the app has no prefetching. */
  onPrefetch?: (href: string) => void;
  onSignOut: () => void;
  /** Override the canonical icon mark only when another product has a distinct identity. */
  logo?: React.ReactNode;
  /** Override the canonical wordmark only when another product has a distinct identity. */
  mobileLogo?: React.ReactNode;
  /** Hover copy for a locked item. Required only if any entry is locked. */
  lockedHint?: string;
  /** Accessible name for the rail — "Main navigation" unless the app differs. */
  ariaLabel?: string;
  /** Temporarily render a mode without replacing the user's persisted choice.
   *  Used by focused flows such as onboarding that need the content width back;
   *  removing the prop restores the saved mode immediately. */
  forcedMode?: SidebarMode;
  /** Resting mode when this app has no saved preference. */
  defaultMode?: SidebarMode;
  /** App-specific preference register. A new key intentionally rolls every
   *  user onto a new default once while preserving later manual choices. */
  modeStorageKey?: string;
}

export const AppSidebar: React.FC<AppSidebarProps> = ({
  entries,
  isActive,
  onPrefetch,
  onSignOut,
  logo = DEFAULT_LOGO_MARK,
  mobileLogo = DEFAULT_WORDMARK,
  lockedHint = 'Not available here',
  ariaLabel = 'Main navigation',
  forcedMode,
  defaultMode = 'expanded',
  modeStorageKey = DEFAULT_SIDEBAR_MODE_KEY,
}) => {
  const mainNav = useMemo(() => entries.filter((e) => (isAppNavItem(e) ? !e.bottom : true)), [entries]);
  const bottomNav = useMemo(() => entries.filter((e) => isAppNavItem(e) && e.bottom) as AppNavItem[], [entries]);

  const [mode, setMode] = useState<SidebarMode>(() => readSidebarMode(modeStorageKey, defaultMode));
  const [isHovered, setIsHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const effectiveMode = forcedMode ?? mode;
  const isExpanded = effectiveMode === 'expanded' || (effectiveMode === 'hover' && isHovered);
  const isHoverExpanded = effectiveMode === 'hover' && isHovered;

  // --app-content-inset drives the LAYOUT. We only push content for the user's
  // CHOSEN mode, never for the hover preview: a hover-expanded sidebar floats
  // OVER the content, because pushing on hover jiggles text on every accidental
  // mouse-stray. The mode→inset mapping lives in tokens.css; this effect writes
  // ONLY the attribute, so no pixel value comes from JS and it cannot disagree
  // with the pre-hydration script in index.html.
  useEffect(() => {
    document.documentElement.setAttribute('data-sidebar-mode', effectiveMode);
  }, [effectiveMode]);

  // Persist only the mode the user CHOSE. A forced onboarding presentation must
  // never silently replace that choice with `collapsed`.
  useEffect(() => {
    persistSidebarState(mode, mode === 'expanded', modeStorageKey);
  }, [mode, modeStorageKey]);

  const changeMode = useCallback((newMode: SidebarMode) => {
    setMode(newMode);
    if (newMode !== 'hover') setIsHovered(false);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (forcedMode) return;
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
        e.preventDefault();
        setMode((prev) => {
          const cycle: SidebarMode[] = ['expanded', 'hover', 'collapsed'];
          return cycle[(cycle.indexOf(prev) + 1) % cycle.length];
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [forcedMode]);

  const handleMouseEnter = useCallback(() => {
    if (effectiveMode !== 'hover') return;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovered(true);
  }, [effectiveMode]);

  const handleMouseLeave = useCallback(() => {
    if (effectiveMode !== 'hover') return;
    hoverTimeoutRef.current = setTimeout(() => setIsHovered(false), HOVER_COLLAPSE_DELAY);
  }, [effectiveMode]);

  useEffect(
    () => () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    },
    [],
  );

  return (
    <>
      {/* Brand block tracks the rail mode, so the wordmark and navigation share
          one deliberate left edge instead of splitting the logo from the rail. */}
      <Link
        to="/"
        aria-label="Home"
        className={cn(
          'focus-ring fixed left-0 top-0 z-shell-logo hidden items-center overflow-hidden border-b border-sidebar-border bg-sidebar px-[18px] transition-[width] duration-200 ease-out md:flex',
          isExpanded ? 'justify-start' : 'justify-center',
        )}
        style={{ width: isExpanded ? EXPANDED_W : COLLAPSED_W, height: 'var(--app-topnav-h)' }}
      >
        {isExpanded ? mobileLogo : logo}
      </Link>

      {/* Desktop rail — below the logo. The rail's visual width tracks
          isExpanded (so hover grows the panel), while the content inset on
          :root tracks mode only (so hover floats instead of pushing). */}
      <nav
        aria-label={ariaLabel}
        className={cn(
          'fixed bottom-0 left-0 z-shell hidden flex-col overflow-hidden border-r border-sidebar-border bg-sidebar transition-[width] duration-200 ease-out md:flex',
          isHoverExpanded ? 'shadow-xl' : 'shadow-sm',
        )}
        style={{ top: 'var(--app-topnav-h)', width: isExpanded ? EXPANDED_W : COLLAPSED_W }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex-1 overflow-x-hidden overflow-y-auto px-[9.5px] pb-2 pt-4">
          <div className="flex flex-col">
            {mainNav.map((entry, i) =>
              isAppNavItem(entry) ? (
                <NavLink
                  key={entry.key}
                  item={entry}
                  isActive={!entry.isExternal && isActive(entry.href)}
                  isExpanded={isExpanded}
                  badge={entry.badge}
                  onPrefetch={onPrefetch}
                  lockedHint={lockedHint}
                />
              ) : isAppNavGroup(entry) ? (
                <DesktopNavGroup
                  key={entry.key}
                  group={entry}
                  isExpanded={isExpanded}
                  isActive={isActive}
                  onPrefetch={onPrefetch}
                  lockedHint={lockedHint}
                />
              ) : (
                <SectionLabel key={`section-${i}`} title={entry.title} isExpanded={isExpanded} />
              ),
            )}
          </div>
        </div>

        <div className="px-[9.5px] py-3">
          {bottomNav.map((item) => (
            <NavLink
              key={item.key}
              item={item}
              isActive={!item.isExternal && isActive(item.href)}
              isExpanded={isExpanded}
              onPrefetch={onPrefetch}
              lockedHint={lockedHint}
            />
          ))}
          {!forcedMode && (
            <>
              <div className="my-1.5 mx-2 border-t border-sidebar-border" />
              <SidebarControl mode={mode} onChangeMode={changeMode} isExpanded={isExpanded} />
            </>
          )}
        </div>
      </nav>

      {/* Mobile header */}
      <div className="fixed left-0 right-0 top-0 z-mobile-bar flex h-16 items-center border-b border-sidebar-border bg-sidebar px-3 md:hidden">
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="focus-ring rounded-lg p-2 text-sidebar-foreground transition-colors hover:bg-sidebar-accent"
          aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>
        <Link to="/" className="ml-3 rounded">
          {mobileLogo}
        </Link>
      </div>

      {/* Mobile drawer — pure CSS enter/exit, always mounted so the exit
          transition plays; pointer-events gate the off state. */}
      <div
        className={cn(
          'fixed inset-0 z-mobile-scrim bg-foreground transition-opacity duration-300 md:hidden',
          mobileOpen ? 'opacity-30' : 'opacity-0 pointer-events-none',
        )}
        aria-hidden={!mobileOpen}
        onClick={() => setMobileOpen(false)}
      />
      <nav
        id="mobile-nav"
        aria-label="Mobile navigation"
        className={cn(
          'fixed left-0 top-0 z-mobile-panel flex h-full w-72 flex-col border-r border-sidebar-border bg-sidebar md:hidden',
          'transition-transform duration-200 ease-out motion-reduce:transition-none',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-hidden={!mobileOpen}
      >
        <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-3">
          <Link to="/" onClick={() => setMobileOpen(false)}>
            {mobileLogo}
          </Link>
          <button
            onClick={() => setMobileOpen(false)}
            className="focus-ring rounded-lg p-2 text-sidebar-foreground transition-colors hover:bg-sidebar-accent"
            aria-label="Close navigation menu"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="flex-1 py-4 overflow-y-auto px-2">
          <div className="space-y-px">
            {mainNav.map((entry, i) =>
              isAppNavItem(entry) ? (
                <MobileNavLink
                  key={entry.key}
                  item={entry}
                  isActive={!entry.isExternal && isActive(entry.href)}
                  onNavigate={() => setMobileOpen(false)}
                />
              ) : isAppNavGroup(entry) ? (
                <MobileNavGroup
                  key={entry.key}
                  group={entry}
                  isActive={isActive}
                  onNavigate={() => setMobileOpen(false)}
                />
              ) : entry.title ? (
                <div key={`section-${i}`} className="px-3 pt-4 pb-1">
                  <span className="text-micro font-medium text-sidebar-foreground/55">{entry.title}</span>
                </div>
              ) : (
                <div key={`section-${i}`} className="my-2 mx-3 border-t border-sidebar-border" />
              ),
            )}
          </div>
        </div>
        <div className="py-4 px-3 border-t border-sidebar-border rounded-br-lg space-y-1">
          {bottomNav.map((item) => (
            <MobileNavLink
              key={item.key}
              item={item}
              isActive={!item.isExternal && isActive(item.href)}
              onNavigate={() => setMobileOpen(false)}
            />
          ))}
          <button
            onClick={onSignOut}
            className="focus-ring flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-primary-foreground"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Sign out
          </button>
        </div>
      </nav>
    </>
  );
};

// ─── Navigation groups ─────────────────────────────────────────
// Expanded rails and the mobile drawer disclose children in place. A permanently
// collapsed rail cannot reveal inline labels, so the same group opens as a
// right-hand flyout. Groups start closed unless they own the current route.

const DesktopNavGroup: React.FC<{
  group: AppNavGroup;
  isExpanded: boolean;
  isActive: (href: string) => boolean;
  onPrefetch?: (href: string) => void;
  lockedHint: string;
}> = ({ group, isExpanded, isActive, onPrefetch, lockedHint }) => {
  const active = group.children.some((item) => !item.isExternal && isActive(item.href));
  const [open, setOpen] = useState(active);
  const Icon = group.icon;

  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  const trigger = (
    <button
      type="button"
      className={cn(
        NAV_LINK_BASE,
        active ? 'text-sidebar-accent-foreground' : NAV_LINK_INACTIVE,
        'w-full',
        !isExpanded && 'justify-center',
      )}
      aria-label={group.title}
    >
      <div className={NAV_ICON_CONTAINER}>
        <Icon className={active ? NAV_ICON_ACTIVE : NAV_ICON_INACTIVE} aria-hidden="true" />
      </div>
      {isExpanded && (
        <>
          <span className="text-sm leading-5 font-medium whitespace-nowrap flex-1 text-left">{group.title}</span>
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} aria-hidden="true" />
        </>
      )}
    </button>
  );

  if (!isExpanded) {
    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start" sideOffset={8} className="w-52 py-1">
          <DropdownMenuLabel className="px-2 py-1.5 text-xs text-ink-supporting">{group.title}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {group.children.map((item) => {
            const childActive = !item.isExternal && isActive(item.href);
            if (item.locked) {
              return (
                <DropdownMenuItem key={item.key} disabled title={lockedHint}>
                  {item.title}
                </DropdownMenuItem>
              );
            }
            const className = cn(
              'group flex w-full items-center gap-2 text-sm',
              childActive && 'bg-accent text-ink-strong',
            );
            const body = <span>{item.title}</span>;
            const child = item.isExternal ? (
              <a
                href={item.href}
                target={item.newTab === false ? undefined : '_blank'}
                rel={item.newTab === false ? undefined : 'noopener noreferrer'}
                className={className}
              >
                {body}
              </a>
            ) : (
              <Link to={item.href} className={className} onMouseEnter={() => onPrefetch?.(item.href)}>
                {body}
              </Link>
            );
            return (
              <DropdownMenuItem key={item.key} asChild>
                {child}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>{trigger}</CollapsibleTrigger>
      <CollapsibleContent className="space-y-px">
        {group.children.map((item) => (
          <NavLink
            key={item.key}
            item={item}
            isActive={!item.isExternal && isActive(item.href)}
            isExpanded
            badge={item.badge}
            onPrefetch={onPrefetch}
            lockedHint={lockedHint}
            nested
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

const MobileNavGroup: React.FC<{
  group: AppNavGroup;
  isActive: (href: string) => boolean;
  onNavigate: () => void;
}> = ({ group, isActive, onNavigate }) => {
  const active = group.children.some((item) => !item.isExternal && isActive(item.href));
  const [open, setOpen] = useState(active);
  const Icon = group.icon;

  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className={cn(
            'focus-ring flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
            active
              ? 'bg-sidebar-accent text-sidebar-accent-foreground'
              : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          )}
        >
          <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="flex-1 text-left">{group.title}</span>
          <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} aria-hidden="true" />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-px">
        {group.children.map((item) => (
          <MobileNavLink
            key={item.key}
            item={item}
            isActive={!item.isExternal && isActive(item.href)}
            onNavigate={onNavigate}
            nested
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

// ─── Sidebar control ────────────────────────────────────────────

const SidebarControl: React.FC<{
  mode: SidebarMode;
  onChangeMode: (mode: SidebarMode) => void;
  isExpanded: boolean;
}> = ({ mode, onChangeMode, isExpanded }) => {
  const [open, setOpen] = useState(false);

  const options: { value: SidebarMode; label: string; icon: React.ReactNode }[] = [
    { value: 'expanded', label: 'Expanded', icon: <PanelLeft className="h-3.5 w-3.5" /> },
    { value: 'collapsed', label: 'Collapsed', icon: <PanelLeftClose className="h-3.5 w-3.5" /> },
    { value: 'hover', label: 'Expand on hover', icon: <MousePointerClick className="h-3.5 w-3.5" /> },
  ];

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        {/* Identical layout to a nav item, so the rail's icons stay on one axis. */}
        <button
          className={cn(NAV_LINK_BASE, NAV_LINK_INACTIVE, 'w-full', !isExpanded && 'justify-center')}
          aria-label="Sidebar control"
        >
          <div className={NAV_ICON_CONTAINER}>
            <SidebarIcon className={NAV_ICON_INACTIVE} aria-hidden="true" />
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align={isExpanded ? 'start' : 'center'}
        sideOffset={8}
        className="w-48 z-overlay py-1"
        onMouseLeave={() => setOpen(false)}
      >
        <DropdownMenuLabel className="text-xs font-medium text-ink-supporting px-3 py-1.5">
          Sidebar control
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => {
              onChangeMode(opt.value);
              setOpen(false);
            }}
            className={cn(
              'relative flex w-full items-center gap-2 px-3 py-1.5 text-sm outline-none transition-colors cursor-pointer',
              mode === opt.value
                ? 'bg-accent text-ink-strong'
                : 'text-ink-content hover:bg-surface-hover hover:text-ink-strong',
            )}
          >
            {mode === opt.value && <div className={ACTIVE_BAR} />}
            {opt.icon}
            {opt.label}
          </button>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ─── Section label ──────────────────────────────────────────────
// Expanded: a quiet sentence-case label (rule 46 — never uppercase).
// Collapsed: a subtle horizontal line.

const SectionLabel: React.FC<{ title: string; isExpanded: boolean }> = ({ title, isExpanded }) => {
  if (!title) return <div className="my-1.5 mx-2 border-t border-sidebar-border" />;
  return (
    <div className="h-8 flex items-center px-2">
      {isExpanded ? (
        <span className="text-micro font-medium text-sidebar-foreground/55">{title}</span>
      ) : (
        <div className="w-full border-t border-sidebar-border" />
      )}
    </div>
  );
};

// ─── Nav link ───────────────────────────────────────────────────
// Shared constants for ALL styling, so expanded and collapsed are identical
// except for text visibility. No conditional classes on layout properties.

const NavLink: React.FC<{
  item: AppNavItem;
  isActive: boolean;
  badge?: number;
  isExpanded: boolean;
  onPrefetch?: (href: string) => void;
  lockedHint: string;
  nested?: boolean;
}> = ({ item, isActive, badge, isExpanded, onPrefetch, lockedHint, nested = false }) => {
  const Icon = item.icon;
  const showBadge = badge !== undefined && badge > 0;

  const linkContent = (
    <>
      {isActive && <div className={ACTIVE_BAR} />}
      <div className={NAV_ICON_CONTAINER}>
        {Icon && <Icon className={isActive ? NAV_ICON_ACTIVE : NAV_ICON_INACTIVE} />}
      </div>
      {isExpanded && <span className="text-sm leading-5 font-medium whitespace-nowrap flex-1">{item.title}</span>}
      {showBadge && isExpanded && (
        <span className="flex-shrink-0 text-detail tabular-nums text-sidebar-foreground/55">{badge}</span>
      )}
    </>
  );

  // Never warm a locked route — the page would immediately bounce.
  const mouseHandlers =
    item.locked || !onPrefetch
      ? {}
      : {
          onMouseEnter: () => {
            if (!item.isExternal) onPrefetch(item.href);
          },
        };

  if (item.locked) {
    // A <span>, never a <Link>: nothing to ⌘-click or open in a new tab into a
    // page that would bounce. Still in the a11y tree and announced disabled;
    // out of the tab order, like every disabled control.
    return (
      <SimpleTooltip
        content={isExpanded ? lockedHint : `${item.title} — ${lockedHint.toLowerCase()}`}
        side="right"
        align="center"
      >
        <span
          role="link"
          aria-disabled="true"
          aria-label={`${item.title} — ${lockedHint.toLowerCase()}`}
          tabIndex={-1}
          className={cn(NAV_LINK_BASE, NAV_LINK_LOCKED, !isExpanded && 'justify-center')}
        >
          {linkContent}
        </span>
      </SimpleTooltip>
    );
  }

  const link = item.isExternal ? (
    <a
      href={item.href || '#'}
      target={item.newTab === false ? undefined : '_blank'}
      rel={item.newTab === false ? undefined : 'noopener noreferrer'}
      aria-label={item.title}
      data-nav-surface="rail"
      data-nav-key={item.key}
      {...mouseHandlers}
      className={cn(NAV_LINK_BASE, NAV_LINK_INACTIVE, !isExpanded && 'justify-center')}
    >
      {linkContent}
    </a>
  ) : (
    <Link
      to={item.href}
      aria-label={item.title}
      aria-current={isActive ? 'page' : undefined}
      data-nav-surface="rail"
      data-nav-key={item.key}
      {...mouseHandlers}
      className={cn(
        NAV_LINK_BASE,
        isActive ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE,
        !isExpanded && 'justify-center',
        nested && isExpanded && 'pl-7',
      )}
    >
      {linkContent}
    </Link>
  );

  // Collapsed rail: the icon alone can't name the destination, so the label
  // rides a canonical hover hint (Radix portals + flips it, so the rail can't
  // clip it). Expanded: the label is already on screen, no hint.
  if (isExpanded) return link;
  return (
    <SimpleTooltip content={item.title} side="right" align="center">
      {link}
    </SimpleTooltip>
  );
};

const MobileNavLink: React.FC<{
  item: AppNavItem;
  isActive: boolean;
  onNavigate: () => void;
  nested?: boolean;
}> = ({ item, isActive, onNavigate, nested = false }) => {
  const Icon = item.icon;
  const linkClass = cn(
    'flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-colors',
    nested && 'pl-9',
    isActive
      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
      : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
  );
  const body = (
    <>
      {Icon && <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />}
      <span className="text-sm font-medium">{item.title}</span>
    </>
  );

  if (item.isExternal) {
    return (
      <a
        href={item.href || '#'}
        target={item.newTab === false ? undefined : '_blank'}
        rel={item.newTab === false ? undefined : 'noopener noreferrer'}
        data-nav-surface="rail"
        data-nav-key={item.key}
        onClick={onNavigate}
        className={linkClass}
      >
        {body}
      </a>
    );
  }
  return (
    <Link
      to={item.href}
      aria-current={isActive ? 'page' : undefined}
      onClick={onNavigate}
      className={linkClass}
      data-nav-surface="rail"
      data-nav-key={item.key}
    >
      {body}
    </Link>
  );
};

export default AppSidebar;
